/**
 * Dove un apparecchio nuovo chiede un codice di attivazione, e con quale chiave.
 *
 * **Sono scritti qui, nel sorgente.** È l'unico indirizzo che il player conosce oltre al gateway, e
 * al primo avvio non c'è ancora un operatore a cui chiederlo: scoprire a chi appartiene è tutto il
 * senso della procedura. Quindi o è compilato dentro l'app, o l'app non può cominciare.
 *
 * ## Perché la chiave può stare in un repository
 *
 * **Non è un segreto, per disegno.** Un valore compilato dentro un'app distribuita su Tizen e
 * Android si estrae dal pacchetto in pochi minuti, quindi trattarla come un segreto sarebbe
 * fingere. Serve a una cosa sola: tenere fuori il traffico casuale e dare un appiglio al limite di
 * frequenza. Nessuna decisione si appoggia su di essa — la difesa vera è che il codice vive novanta
 * secondi ed è il rivenditore, autenticato dal proprio backend, a depositare le credenziali
 * (SPEC §7).
 *
 * Nasconderla in una variabile d'ambiente avrebbe dato la stessa esposizione con in più un modo di
 * compilare un pacchetto che non funziona: è esattamente quel che è successo la prima volta.
 */

/** Il servizio di accoppiamento in produzione. */
export const PAIRING_URL = 'https://pairing.ath.cx';

/** La chiave del player. Vedi sopra: pubblica per costruzione. */
export const PAIRING_PLAYER_KEY = '71a876cc2d36fcb33036aa854518f504a6bbe148b84880be';

/**
 * L'indirizzo e la chiave effettivi di questa compilazione.
 *
 * Le variabili d'ambiente **non servono a nascondere**: servono a chi fa girare un proprio servizio
 * di accoppiamento — un'installazione separata, un collaudo — e non vuole ricompilare cambiando una
 * costante. Assenti, valgono quelle qui sopra, che sono i valori veri.
 *
 * In sviluppo l'indirizzo diventa **la stessa origine**: il servizio non manda intestazioni CORS —
 * su un televisore non servono, l'app è impacchettata e non ha un'origine — quindi da un browser la
 * richiesta partirebbe e la risposta non si potrebbe leggere. Il server di sviluppo gira
 * `/v1/requests` verso `PLAYER_PAIRING` (vedi `vite.config.ts`), e per il browser è tutto casa sua.
 */
export interface PairingEnv {
  readonly VITE_PAIRING_URL?: string;
  readonly VITE_PAIRING_KEY?: string;
  readonly DEV?: boolean;
}

export function pairingConfig(env: PairingEnv = import.meta.env): {
  baseUrl: string;
  playerKey: string;
} {
  const override = env.VITE_PAIRING_URL ?? '';
  const key = env.VITE_PAIRING_KEY ?? '';

  const baseUrl = override.length > 0 ? override : env.DEV === true ? '' : PAIRING_URL;

  return { baseUrl, playerKey: key.length > 0 ? key : PAIRING_PLAYER_KEY };
}
